import React from 'react';
import { Check } from 'lucide-react';

export const IRON_CLUB_NUMBERS = ['4', '5', '6', '7', '8', '9', 'PW', 'W'];
export const DEFAULT_IRON_SET = ['4', '5', '6', '7', '8', '9', 'PW', 'W'];
export const STANDARD_SET_SIZE = DEFAULT_IRON_SET.length; // 8

/**
 * Multi-select club number picker for iron products.
 * Users can build a full set or pick individual irons.
 *
 * Props:
 *   value:     Array<string> of selected club numbers (e.g. ['5','6','7','8','9','PW'])
 *   onChange:  (next: Array<string>) => void
 *   perIronPrice: number
 */
export default function IronClubSelector({ value = [], onChange, perIronPrice = 0 }) {
  const toggle = (num) => {
    const next = value.includes(num)
      ? value.filter((n) => n !== num)
      : [...value, num].sort((a, b) => {
          // preserve canonical club order
          const order = IRON_CLUB_NUMBERS;
          return order.indexOf(a) - order.indexOf(b);
        });
    onChange(next);
  };

  const setAll = () => onChange([...DEFAULT_IRON_SET]);
  const clear = () => onChange([]);
  const setStandardSixSet = () => onChange(['5', '6', '7', '8', '9', 'PW']);

  const totalForSelection = perIronPrice * value.length;

  return (
    <div data-testid="iron-club-selector">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#334155] uppercase tracking-widest">
          Club Numbers <span className="text-[#D4A55A] ml-1">*</span>
        </span>
        <span className="text-xs text-gray-600">
          {value.length === 0 ? (
            <span className="text-[#EF4444]">Select at least one</span>
          ) : (
            <>
              {value.length} {value.length === 1 ? 'iron' : 'irons'} ·{' '}
              <span className="font-semibold text-gray-900">${totalForSelection.toFixed(2)}</span>
            </>
          )}
        </span>
      </div>

      {/* Club number pills */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-3">
        {IRON_CLUB_NUMBERS.map((num) => {
          const on = value.includes(num);
          return (
            <button
              key={num}
              type="button"
              onClick={() => toggle(num)}
              className={`relative h-14 rounded-lg border-2 font-bold text-lg transition-all ${
                on
                  ? 'bg-[#334155] text-white border-[#334155] shadow-sm'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`iron-club-${num}`}
            >
              {num}
              {on && (
                <Check className="absolute top-1 right-1 w-3 h-3" />
              )}
            </button>
          );
        })}
      </div>

      {/* Shortcut presets */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
          Quick select:
        </span>
        <button
          type="button"
          onClick={setAll}
          className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 hover:border-[#334155] hover:bg-gray-50"
          data-testid="iron-preset-full"
        >
          Full Set (4-W)
        </button>
        <button
          type="button"
          onClick={setStandardSixSet}
          className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 hover:border-[#334155] hover:bg-gray-50"
          data-testid="iron-preset-standard"
        >
          Standard (5-PW)
        </button>
        {value.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-gray-500 hover:text-gray-800 ml-auto"
            data-testid="iron-preset-clear"
          >
            Clear
          </button>
        )}
      </div>

      <p className="text-[11px] text-gray-500 mt-2">
        Per iron: <span className="font-semibold text-gray-800">${perIronPrice.toFixed(2)}</span>
        {' · '}Price scales with the number of clubs selected.
      </p>
    </div>
  );
}
